import {Group, Stack, TextInput, createStyles} from '@mantine/core';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import LeafTypeSelector from 'data-frames/header-menu/LeafTypeSelector';
import PrimitiveValueEditor, {SupportedLeafType} from 'data-frames/PrimitiveValueEditor';
import {METADATA_ALLOWED_LEAF_TYPES} from 'samples/plate-maps/annotate-plate-modal/MetadataLayerSettings';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {
  MAX_PLATE_METADATA_FIELDS,
  PlateMetadataFieldEntry,
  PlateMetadataFieldsAction,
} from './ScratchPlateDesignModalUtil';

export interface PlateMetadataFieldsSectionProps {
  fields: ReadonlyArray<PlateMetadataFieldEntry>;
  onAction: (action: PlateMetadataFieldsAction) => void;
}

/** Lets the user define up to MAX_PLATE_METADATA_FIELDS whole-plate metadata fields (name, type, value). */
export default function PlateMetadataFieldsSection({
  fields,
  onAction,
}: PlateMetadataFieldsSectionProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack spacing="xs">
      <FieldLabel label="Plate metadata" labelStyle={FieldLabel.STYLES.LABEL} />
      {fields.map((field) => (
        <Group key={field.id} spacing="xs" align="flex-start" noWrap={true}>
          <Stack spacing="xs" className={classes.fieldBox}>
            <TextInput
              size="md"
              label="Field name"
              withAsterisk={true}
              placeholder="Field name"
              value={field.name}
              onChange={(e) => {
                onAction({type: 'updateName', id: field.id, name: e.target.value});
              }}
            />
            <FieldLabel label="Type " isRequired={true} />
            <LeafTypeSelector
              selectedType={field.type}
              allowedTypes={METADATA_ALLOWED_LEAF_TYPES}
              ariaLabel="Type"
              onChange={(fieldType) => {
                onAction({type: 'updateType', id: field.id, fieldType});
              }}
            />
            <FieldLabel label="Value " isRequired={true} />
            <div className={classes.valueEditor} role="group" aria-label="Value">
              <PrimitiveValueEditor
                // Remount on type change so the editor's internal value state can't carry a value of
                // the previous (now type-incompatible) primitive type into the new editor.
                key={field.type}
                // Safe cast: selectable types are constrained by METADATA_ALLOWED_LEAF_TYPES.
                leafType={field.type as SupportedLeafType}
                value={field.value}
                onChange={(value) => {
                  onAction({type: 'updateValue', id: field.id, value});
                }}
              />
            </div>
          </Stack>
          <div className={classes.deleteIcon}>
            <DeleteIcon
              icon="trash-can"
              altText={`Remove metadata field ${field.name}`}
              onClick={() => {
                onAction({type: 'remove', id: field.id});
              }}
            />
          </div>
        </Group>
      ))}
      <AddItemButton
        label="Add metadata field"
        disabled={fields.length >= MAX_PLATE_METADATA_FIELDS}
        onClick={() => {
          onAction({type: 'add'});
        }}
      />
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  fieldBox: {
    flex: 1,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
    padding: theme.spacing.sm,
  },
  deleteIcon: {
    marginTop: theme.spacing.sm,
  },
  // PrimitiveValueEditor's internal input/button heights are hardcoded by the generalized step
  // config editor it delegates to and aren't exposed as a prop, so height is normalized here to
  // match the Name/Type inputs above.
  valueEditor: {
    '& > div': {
      marginBottom: 0,
    },
    '& input, & button': {
      height: '42px !important',
      fontSize: theme.fontSizes.md,
    },
  },
}));
